import { useMemo } from 'react'
import {
  PiArrowBendUpLeft,
  PiArrowBendDoubleUpLeft,
  PiArrowBendUpRight,
  PiBell,
  PiChartLineUp,
  PiEye,
  PiEyeSlash,
  PiPath,
  PiTrash,
} from 'react-icons/pi'
import { ComposeMode } from './ComposePanel'
import { EmailAction } from 'components/Email'

export type { EmailAction }

/** Shared callback props for email deal actions, used by action bar, message, and menu. */
export interface EmailDealActionCallbacks {
  dealId?: number | null
  onAssignToDeal?: () => void
  onRemoveFromDeal?: () => void
  onCreateDeal?: () => void
  onUpdateSalesStage?: () => void
  onScheduleReminder?: () => void
  onHideThread?: () => void
  onUnhideThread?: () => void
}

interface EmailActionContext extends EmailDealActionCallbacks {
  onCompose?: (mode: ComposeMode) => void
  dealId: number | null
  /** React element for the deal search submenu content. */
  assignDealSubContent?: React.ReactNode
  isHidden?: boolean
  isOwnThread?: boolean
}

/** Create thread-level actions, varying based on deal assignment state. */
function createEmailThreadActions(ctx: EmailActionContext): EmailAction[] {
  const actions: EmailAction[] = []

  // Only show compose actions (reply, reply all, forward) for user's own threads
  if (ctx.isOwnThread === true) {
    const composeDisabled = !ctx.onCompose
    actions.push(
      {
        label: 'Reply',
        icon: PiArrowBendUpLeft,
        onClick: () => ctx.onCompose?.('reply'),
        disabled: composeDisabled,
      },
      {
        label: 'Reply All',
        icon: PiArrowBendDoubleUpLeft,
        onClick: () => ctx.onCompose?.('reply_all'),
        disabled: composeDisabled,
      },
      {
        label: 'Forward',
        icon: PiArrowBendUpRight,
        onClick: () => ctx.onCompose?.('forward'),
        disabled: composeDisabled,
        hasSeparatorAfter: true,
      }
    )
  }

  actions.push({
    label: 'Schedule a reminder',
    icon: PiBell,
    onClick: () => ctx.onScheduleReminder?.(),
    disabled: !ctx.onScheduleReminder,
  })

  // Hide/unhide action — mutually exclusive based on is_hidden state
  if (ctx.isHidden) {
    actions.push({
      label: 'Unhide thread',
      icon: PiEye,
      onClick: () => ctx.onUnhideThread?.(),
      disabled: !ctx.onUnhideThread,
      hasSeparatorAfter: true,
    })
  } else {
    actions.push({
      label: 'Hide thread',
      icon: PiEyeSlash,
      onClick: () => ctx.onHideThread?.(),
      disabled: !ctx.onHideThread,
      hasSeparatorAfter: true,
    })
  }

  if (ctx.dealId) {
    // Thread has a deal assigned
    actions.push(
      {
        label: 'Update sales stage',
        icon: PiChartLineUp,
        onClick: () => ctx.onUpdateSalesStage?.(),
        disabled: !ctx.onUpdateSalesStage,
      },
      {
        label: 'Remove from deal',
        icon: PiTrash,
        onClick: () => ctx.onRemoveFromDeal?.(),
        disabled: !ctx.onRemoveFromDeal,
      }
    )
  } else {
    // Thread has no deal assigned
    actions.push(
      {
        label: 'Create a deal',
        icon: PiChartLineUp,
        onClick: () => ctx.onCreateDeal?.(),
        disabled: !ctx.onCreateDeal,
      },
      {
        label: 'Assign to deal',
        icon: PiPath,
        onClick: () => ctx.onAssignToDeal?.(),
        disabled: !ctx.onAssignToDeal,
        subContent: ctx.assignDealSubContent,
      }
    )
  }

  return actions
}

/** Build an EmailActionContext from deal callbacks and optional compose handler. */
export function buildActionContext(
  dealCallbacks: EmailDealActionCallbacks,
  onCompose?: (mode: ComposeMode) => void,
  assignDealSubContent?: React.ReactNode,
  isHidden?: boolean,
  isOwnThread?: boolean
): EmailActionContext {
  const { dealId, ...rest } = dealCallbacks
  return {
    ...rest,
    onCompose,
    dealId: dealId ?? null,
    assignDealSubContent,
    isHidden,
    isOwnThread,
  }
}

/** Memoized email thread actions based on deal context. */
export function useEmailThreadActions(ctx: EmailActionContext): EmailAction[] {
  return useMemo(
    () => createEmailThreadActions(ctx),
    [
      ctx.onCompose,
      ctx.dealId,
      ctx.onAssignToDeal,
      ctx.onRemoveFromDeal,
      ctx.onCreateDeal,
      ctx.onUpdateSalesStage,
      ctx.onScheduleReminder,
      ctx.onHideThread,
      ctx.onUnhideThread,
      ctx.isHidden,
      ctx.isOwnThread,
      ctx.assignDealSubContent,
    ]
  )
}
